import { Badge, Button, Divider, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { dataEHoraPorExtenso, dataSimples, moeda } from '@/comuns/formato';
import { quantidade, unidadeLabel } from '@/comuns/unidades';
import type { PedidoDeCompra, StatusDoPedido } from '@/data/tipos';

export const STATUS: Record<StatusDoPedido, { rotulo: string; tom: 'neutral' | 'info' | 'warning' | 'success' | 'danger' }> = {
    rascunho: { rotulo: 'Rascunho', tom: 'neutral' },
    enviado: { rotulo: 'Enviado', tom: 'info' },
    parcial: { rotulo: 'Recebido em parte', tom: 'warning' },
    recebido: { rotulo: 'Recebido', tom: 'success' },
    cancelado: { rotulo: 'Cancelado', tom: 'danger' },
};

export interface DetalheDoPedidoProps {
    pedido: PedidoDeCompra | null;
    onClose: () => void;
    onEditar: (pedido: PedidoDeCompra) => void;
    onReceber: (pedido: PedidoDeCompra) => void;
    onCancelar: (pedido: PedidoDeCompra) => void;
}

export function DetalheDoPedido({ pedido, onClose, onEditar, onReceber, onCancelar }: DetalheDoPedidoProps) {
    if (!pedido) return null;

    const status = STATUS[pedido.status];
    const emAberto = pedido.status === 'rascunho' || pedido.status === 'enviado' || pedido.status === 'parcial';
    /* Editar só antes de chegar mercadoria: mexer no que já entrou faria o
       pedido discordar do extrato do estoque. */
    const podeEditar = pedido.status === 'rascunho' || pedido.status === 'enviado';

    return (
        <Modal
            open onClose={onClose} width={620}
            title={`Pedido ${pedido.numero}`}
            description={`${pedido.fornecedor} · ${status.rotulo}`}
            footer={
                <>
                    {podeEditar && (
                        <Button variant="ghost" iconLeft={<Icon name="pencil" size={15} />} onClick={() => onEditar(pedido)}>Editar</Button>
                    )}
                    {emAberto && (
                        <Button variant="ghost" iconLeft={<Icon name="ban" size={15} />} onClick={() => onCancelar(pedido)}>Cancelar pedido</Button>
                    )}
                    <span style={{ marginLeft: 'auto' }} />
                    <Button variant="secondary" onClick={onClose}>Fechar</Button>
                    {emAberto && pedido.status !== 'rascunho' && (
                        <Button iconLeft={<Icon name="package-plus" size={15} />} onClick={() => onReceber(pedido)}>Receber</Button>
                    )}
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '10px 20px', fontSize: 13 }}>
                    <dt style={{ color: 'var(--text-secondary)' }}>Situação</dt>
                    <dd style={{ margin: 0, textAlign: 'right' }}><Badge tone={status.tom} dot>{status.rotulo}</Badge></dd>
                    <dt style={{ color: 'var(--text-secondary)' }}>Criado</dt>
                    <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500 }}>
                        {dataEHoraPorExtenso(pedido.criadoEm)}{pedido.criadoPor ? ` · por ${pedido.criadoPor}` : ''}
                    </dd>
                    <dt style={{ color: 'var(--text-secondary)' }}>Previsão de entrega</dt>
                    <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500 }}>
                        {pedido.previsaoEntrega ? dataSimples(pedido.previsaoEntrega) : 'sem previsão'}
                    </dd>
                    {pedido.recebidoEm && (
                        <>
                            <dt style={{ color: 'var(--text-secondary)' }}>Recebido em</dt>
                            <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500 }}>{dataSimples(pedido.recebidoEm.slice(0, 10))}</dd>
                        </>
                    )}
                    {pedido.observacoes && (
                        <>
                            <dt style={{ color: 'var(--text-secondary)' }}>Observações</dt>
                            <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500 }}>{pedido.observacoes}</dd>
                        </>
                    )}
                </dl>

                <Divider label="Itens" spacing={2} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {pedido.itens.map((item) => {
                        const falta = item.quantidade - item.quantidadeRecebida;
                        return (
                            <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                    <span style={{ display: 'block', fontWeight: 500 }}>
                                        {quantidade(item.quantidade)} {unidadeLabel(item.quantidade, item.unidade)} · {item.produto}
                                    </span>
                                    <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)' }}>
                                        {moeda(item.custoUnitarioCentavos)} cada
                                        {/* Preço combinado diferente do cadastro é o que
                                            explica a margem mudar no mês seguinte. */}
                                        {item.custoUnitarioCentavos !== item.custoAtualCentavos
                                            && ` · cadastro hoje: ${moeda(item.custoAtualCentavos)}`}
                                    </span>
                                </span>
                                {item.quantidadeRecebida > 0 && (
                                    <Badge tone={falta > 0 ? 'warning' : 'success'} dot>
                                        {falta > 0 ? `faltam ${quantidade(falta)}` : 'recebido'}
                                    </Badge>
                                )}
                                <span style={{ width: 110, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                    {moeda(item.quantidade * item.custoUnitarioCentavos)}
                                </span>
                            </div>
                        );
                    })}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 10, borderTop: '1px solid var(--border-subtle)', fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Total do pedido</span>
                        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{moeda(pedido.totalCentavos)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Já recebido</span>
                        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{moeda(pedido.recebidoCentavos)}</span>
                    </div>
                </div>

                {pedido.status === 'rascunho' && (
                    <Alert tone="info" dismissible={false} title="Ainda é rascunho">
                        Rascunho não recebe mercadoria: enviar o pedido é o que declara que a clínica está esperando por ele.
                    </Alert>
                )}
            </div>
        </Modal>
    );
}
